const KnownSites = {
    prod: {
        'otrapadel.no': 'otrapadel',
        'pawer.no': 'pawer',
        'booking.jaroshund.no': 'jaros',
        'trondheimhundehall.no': 'ekra'
    },
    staging: {
        'booking.smorgrav.org': 'skotvoll'
    },
    local: {}
};
/**
 * Emulator host/ports, matching the `emulators` block in
 * apps/functions/firebase.json and the `default` alias in .firebaserc.
 * Exported so test harnesses and the app's emulator wiring agree on one source.
 */
const EMULATOR_PROJECT_ID = 'demo-velpr-local';
const EMULATOR_HOST = '127.0.0.1';
const EMULATOR_PORTS = {
    functions: 5001,
    firestore: 8080,
    auth: 9099,
    storage: 9199,
    ui: 4000
};
const EMULATOR_FUNCTIONS_BASE = `http://${EMULATOR_HOST}:${EMULATOR_PORTS.functions}/${EMULATOR_PROJECT_ID}/europe-west1`;
const makeEndpoints = (runContext) => {
    switch (runContext.mode) {
        case 'production':
            return {
                siteUrl: (siteId) => {
                    const customHostname = Object.entries(KnownSites.prod).find((entry) => entry[1] === siteId);
                    if (customHostname) {
                        return new URL('https://' + customHostname[0]);
                    }
                    return new URL('https://velpr.online/' + siteId);
                },
                wwwUrl: 'https://www.velpr.online',
                functions2: 'https://europe-west1-hundehall.cloudfunctions.net/api3',
                iot: 'https://iot.velpr.online'
            };
        case 'staging':
            return {
                siteUrl: (siteId) => {
                    const customHostname = Object.entries(KnownSites.staging).find((entry) => entry[1] === siteId);
                    if (customHostname) {
                        return new URL('https://' + customHostname[0]);
                    }
                    return new URL('https://hundehall-dev.web.app/' + siteId);
                },
                wwwUrl: 'https://velprw3-dev.web.app',
                functions2: 'https://europe-west1-hundehall-dev.cloudfunctions.net/api3',
                // No iot host in staging — devices poll the api3 function directly.
                iot: 'https://europe-west1-hundehall-dev.cloudfunctions.net/api3'
            };
        case 'development':
            return {
                siteUrl: (siteId) => new URL('http://localhost:3000/' + siteId),
                wwwUrl: 'http://localhost:4321',
                functions2: 'https://europe-west1-hundehall-dev.cloudfunctions.net/api3',
                iot: 'https://europe-west1-hundehall-dev.cloudfunctions.net/api3'
            };
        // Emulator suite. Function URLs are plain HTTP and must carry the
        // {project}/{region} path segments — the emulator routes on them, so
        // 'http://127.0.0.1:5001/api3' 404s. Project id comes from .firebaserc
        // (`default`), region from the onRequest definitions in api.ts.
        case 'test':
            return {
                siteUrl: (siteId) => new URL('http://localhost:3000/' + siteId),
                wwwUrl: 'http://localhost:4321',
                functions2: `${EMULATOR_FUNCTIONS_BASE}/api3`,
                // No iot host locally — devices (and the device-endpoint tests) poll
                // the api3 mount directly.
                iot: `${EMULATOR_FUNCTIONS_BASE}/api3`
            };
    }
};
const stripTrailingSlash = (str) => {
    return str.endsWith('/') ? str.slice(0, -1) : str;
};
const emptySiteAndRoutingContext = () => {
    return [
        {
            siteId: '',
            siteOrigin: 'https://notarealurl.com',
            sitePrefix: ''
        },
        { redirect: '', 404: false, authAction: false }
    ];
};
const makeSiteAndRoutingCxt = (runCxt, endpointCxt) => {
    const [resultSite, resultRouting] = emptySiteAndRoutingContext();
    if (runCxt.system !== 'app') {
        return [resultSite, resultRouting];
    }
    // Check if hostname is a known site (ie. has a custom domain)
    const knownSites = (runCxt.mode === 'production'
        ? KnownSites.prod
        : runCxt.mode === 'development'
            ? KnownSites.local
            : KnownSites.staging);
    if (knownSites[runCxt.url.hostname]) {
        resultSite.siteId = knownSites[runCxt.url.hostname];
        resultSite.siteOrigin = runCxt.url.origin;
        resultSite.sitePrefix = '';
        return [resultSite, resultRouting];
    }
    // If hostname is not a custom domain name then we need a path segment to identify the site
    const canonicalPath = stripTrailingSlash(runCxt.url.pathname);
    const pathParts = canonicalPath.split('/').filter((part) => part !== '');
    const partCount = pathParts.length;
    const isRoot = partCount === 0;
    // If we don't have a path segment we redirect to the landing page
    if (isRoot) {
        resultRouting.redirect = endpointCxt.wwwUrl;
        return [resultSite, resultRouting];
    }
    // Reserved, site-less app route: the global Firebase email action handler.
    // The project's Action URL points at {baseOrigin}/auth/action, so this path
    // must NOT be parsed as a siteId. Only reached on the base origin — custom
    // domains return above, and we never set the Action URL to a custom domain.
    if (pathParts[0] === 'auth') {
        resultRouting.authAction = true;
        return [resultSite, resultRouting];
    }
    // Also redirect /site and /hall to new format
    if (pathParts[0] === 'site' || pathParts[0] === 'hall') {
        if (partCount === 1) {
            resultRouting[404] = true;
            return [resultSite, resultRouting];
        }
        else {
            resultRouting.redirect = '/' + pathParts.slice(1).join('/');
            return [resultSite, resultRouting];
        }
    }
    // At last first segment in path is the siteId
    const siteId = pathParts[0];
    const siteContext = {
        siteId: siteId,
        siteOrigin: runCxt.url.origin,
        sitePrefix: '/' + siteId
    };
    return [siteContext, resultRouting];
};
const contextFromSiteId = (runCxt, endpoints, siteId) => {
    const [resultSite] = emptySiteAndRoutingContext();
    // Check if hostname is a known site (ie. has a custom domain)
    const knownSites = (runCxt.mode === 'production'
        ? KnownSites.prod
        : runCxt.mode === 'development'
            ? KnownSites.local
            : KnownSites.staging);
    if (knownSites[runCxt.url.hostname]) {
        resultSite.siteId = knownSites[runCxt.url.hostname];
        resultSite.siteOrigin = runCxt.url.origin;
        resultSite.sitePrefix = '';
    }
    return {
        siteId: siteId,
        siteOrigin: endpoints.siteUrl(siteId).origin,
        sitePrefix: '/' + siteId
    };
};
const contextFromUrl = (url, mode, system) => {
    const runCxt = {
        mode: toVelprMode(mode),
        url: url,
        system: system
    };
    const endpoints = makeEndpoints(runCxt);
    const [siteContext, routingContext] = makeSiteAndRoutingCxt(runCxt, endpoints);
    return {
        siteContext,
        routing: routingContext,
        endpoints,
        runContext: runCxt,
        debug: false
    };
};
/**
 * Map a build/run mode string (e.g. Vite's `import.meta.env.MODE`) to a
 * VelprMode.
 *
 * The default is deliberately `production`: an unrecognised mode must never
 * silently downgrade a real deployment to a dev configuration. Every supported
 * mode therefore needs an explicit case — omitting `test` previously made
 * `vite --mode test` resolve to `production`, which pointed the emulator dev
 * server at the live project.
 */
const toVelprMode = (m) => {
    switch (m) {
        case 'production':
            return 'production';
        case 'development':
            return 'development';
        case 'staging':
            return 'staging';
        case 'test':
            return 'test';
        default:
            return 'production';
    }
};
export { contextFromUrl, contextFromSiteId, EMULATOR_FUNCTIONS_BASE, EMULATOR_HOST, EMULATOR_PORTS, EMULATOR_PROJECT_ID, toVelprMode };
//# sourceMappingURL=context.js.map